<template>
  <div class="admin-backgrounds">
    <div class="page-header">
      <h2>{{ $t('legacy.Управление Фонами') }}</h2>
      <button @click="showUploadModal = true" class="btn-primary">
        + {{ $t('legacy.Загрузить Фоны') }}
      </button>
    </div>

    <div v-if="backgrounds.length > 0" class="backgrounds-grid">
      <div v-for="background in backgrounds" :key="background.id" class="background-card">
        <img :src="background.image_path" :alt="background.name" class="background-image" />
        <div class="background-info">
          <h4>{{ background.name }}</h4>
        </div>
        <div class="background-actions">
          <button @click="deleteBackground(background)" class="btn-delete">{{ $t('common.delete') }}</button>
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
       <p>{{ $t('legacy.Нет фонов. Загрузите первый фон.') }}</p>
    </div>

    <div v-if="showUploadModal" class="modal-overlay" @click.self="closeUploadModal">
      <div class="modal-content upload-modal">
         <h3>{{ $t('legacy.Загрузить Фоны') }}</h3>
        <div class="dropzone" @drop.prevent="handleDrop" @dragover.prevent>
          <input 
            ref="fileInput"
            type="file" 
            multiple 
            accept="image/jpeg,image/jpg,image/png"
            @change="handleFileSelect"
            style="display: none"
          />
          <div v-if="uploadFiles.length === 0" class="dropzone-placeholder">
             <p>{{ $t('legacy.Перетащите изображения сюда или') }}</p>
            <button type="button" @click="$refs.fileInput.click()" class="btn-select">
               {{ $t('legacy.Выбрать файлы') }}
            </button>
          </div>
          <div v-else class="files-preview">
            <div v-for="(file, index) in uploadFiles" :key="index" class="file-item">
              <img :src="file.preview" alt="preview" class="file-preview" />
              <input 
                v-model="file.name" 
                class="file-name-input" 
                 :placeholder="$t('legacy.Название фона')"
              />
              <button @click="removeFile(index)" class="btn-remove">×</button>
            </div>
          </div>
        </div>

        <div v-if="isUploading" class="upload-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <p class="progress-text">
             {{ $t('legacy.Загрузка') }}: {{ uploadedCount }} {{ $t('legacy.из') }} {{ uploadFiles.length }} ({{ uploadProgress }}%)
          </p>
        </div>

        <div class="form-actions">
          <button @click="closeUploadModal" class="btn-cancel" :disabled="isUploading">
             {{ $t('common.cancel') }}
          </button>
          <button 
            @click="uploadBackgrounds" 
            class="btn-primary"
            :disabled="uploadFiles.length === 0 || isUploading"
          >
             <span v-if="isUploading">{{ $t('common.loading') }}</span>
             <span v-else>{{ $t('legacy.Загрузить') }} ({{ uploadFiles.length }})</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { adminApi } from '@/api/admin';
import { useToast } from 'vue-toastification';

export default {
  name: 'AdminBackgrounds',
  data() {
    return {
      toast: null,
      backgrounds: [],
      showUploadModal: false,
      uploadFiles: [],
      isUploading: false,
      uploadProgress: 0,
      uploadedCount: 0
    }
  },
  async created() {
    this.toast = useToast();
    await this.loadBackgrounds();
  },
  methods: {
    async loadBackgrounds() {
      try {
        const response = await adminApi.getBackgrounds();
        if (response.data.success) {
          this.backgrounds = response.data.backgrounds;
        }
      } catch (error) {
        console.error('Failed to load backgrounds:', error);
        this.toast.error(this.$t('legacy.Не удалось загрузить фоны'));
      }
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
    },

    handleDrop(event) {
      const files = Array.from(event.dataTransfer.files).filter(file => 
        file.type.startsWith('image/')
      );
      this.processFiles(files);
    },

    processFiles(files) {
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.uploadFiles.push({
            file,
            preview: e.target.result,
            name: file.name.replace(/\.[^/.]+$/, '')
          });
        };
        reader.readAsDataURL(file);
      });
    },

    removeFile(index) {
      this.uploadFiles.splice(index, 1);
    },

    async uploadBackgrounds() {
      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadedCount = 0;
      const totalFiles = this.uploadFiles.length;

      try {
        for (let i = 0; i < this.uploadFiles.length; i++) {
          const fileData = this.uploadFiles[i];
          const formData = new FormData();
          formData.append('image', fileData.file);
          formData.append('name', fileData.name);

          await adminApi.createBackground(formData);
          this.uploadedCount++;
          this.uploadProgress = Math.round((this.uploadedCount / totalFiles) * 100);
        }

        this.toast.success(this.$t('legacy.dynamic.uploadedBackgrounds', { count: totalFiles }));
        await this.loadBackgrounds();
        this.closeUploadModal();
      } catch (error) {
        console.error('Upload error:', error);
        this.toast.error(this.$t('legacy.dynamic.uploadBackgroundsError', {
          uploaded: this.uploadedCount,
          total: totalFiles
        }));
      } finally {
        this.isUploading = false;
      }
    },

    async deleteBackground(background) {
       if (!confirm(this.$t('legacy.dynamic.backgroundDelete', { name: background.name }))) {
        return;
      }

      try {
        await adminApi.deleteBackground(background.id);
        this.toast.success(this.$t('legacy.Фон удален'));
        await this.loadBackgrounds();
      } catch (error) {
        console.error('Failed to delete background:', error);
        this.toast.error(this.$t('legacy.Не удалось удалить фон'));
      }
    },

    closeUploadModal() {
      this.showUploadModal = false;
      this.uploadFiles = [];
      this.uploadProgress = 0;
      this.uploadedCount = 0;
    }
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/global.scss";

.admin-backgrounds {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;

  h2 {
    margin: 0;
  }
}

.backgrounds-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.background-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.background-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.background-info {
  padding: 15px;

  h4 {
    margin: 0 0 5px 0;
    font-size: 16px;
  }
}

.background-actions {
  padding: 0 15px 15px;
  display: flex;
  gap: 10px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 18px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;

  h3 {
    margin: 0 0 20px 0;
  }
}

.upload-modal {
  max-width: 900px;
}

.dropzone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.dropzone-placeholder {
  p {
    margin: 0 0 15px 0;
    color: #666;
  }
}

.files-preview {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.file-item {
  display: grid;
  grid-template-columns: 100px 1fr auto;
  gap: 15px;
  align-items: center;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 6px;
}

.file-preview {
  width: 100px;
  height: 75px;
  object-fit: cover;
  border-radius: 4px;
}

.file-name-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.upload-progress {
  margin: 20px 0;

  .progress-bar {
    height: 8px;
    background: #f0f0f0;
    border-radius: 4px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: #4CAF50;
    transition: width 0.3s;
  }

  .progress-text {
    margin: 10px 0 0 0;
    text-align: center;
    color: #666;
    font-size: 14px;
  }
}

.form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn-primary, .btn-cancel, .btn-delete, .btn-select, .btn-remove {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-primary {
  background: #2196F3;
  color: white;

  &:hover:not(:disabled) {
    background: #1976D2;
  }
}

.btn-select {
  background: #4CAF50;
  color: white;

  &:hover {
    background: #45a049;
  }
}

.btn-cancel {
  background: #f5f5f5;
  color: #333;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }
}

.btn-delete {
  background: #f44336;
  color: white;
  flex: 1;

  &:hover {
    background: #d32f2f;
  }
}

.btn-remove {
  width: 36px;
  height: 36px;
  background: #ff5252;
  color: white;
  font-size: 20px;
  line-height: 1;
  padding: 0;

  &:hover {
    background: #ff1744;
  }
}
</style>
